import React, { useEffect } from 'react';
import { FaTruck, FaClock, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';

const ShippingPolicy = () => {
  useEffect(() => {
    document.title = "Shipping & Delivery Policy - SastaMastaRide";
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <FaTruck className="text-blue-600 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Shipping & Delivery Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700">
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-600">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This website is operated by <strong>SastaMastaRide</strong>, managed by <strong>RKTECH Solutions</strong>.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>Important:</strong> SastaMastaRide is a ridesharing platform. This policy covers trip delivery, 
              pickup services, and related transportation services.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Overview</h2>
            <p className="mb-4">
              SastaMastaRide is a digital platform that connects riders (vehicle owners) with passengers seeking 
              shared transportation services. We do not provide physical shipping of goods. This policy outlines 
              our service delivery terms, including trip scheduling, pickup/drop-off services, and service timelines.
            </p>
            <p>
              By using our platform, you agree to the terms outlined in this shipping and delivery policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Service Delivery Model</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Services Include:</h3>
              <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                <li><strong>Ridesharing Services:</strong> Connecting riders and passengers for shared rides</li>
                <li><strong>Trip Booking:</strong> Digital booking and confirmation services</li>
                <li><strong>Payment Processing:</strong> Secure payment gateway services</li>
                <li><strong>Real-time Tracking:</strong> GPS-based trip tracking services</li>
                <li><strong>Support Services:</strong> Customer support and assistance</li>
              </ul>
            </div>
            <p className="text-gray-700">
              <strong>Note:</strong> We do not own, operate, or control vehicles. All transportation services are 
              provided by independent riders registered on our platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Trip Scheduling & Delivery Duration</h2>
            <div className="bg-blue-50 p-6 rounded-lg mb-4">
              <div className="flex items-start space-x-3">
                <FaClock className="text-blue-600 text-2xl mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Service Timeline</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Booking Confirmation:</strong> Instant (within seconds of payment)</li>
                    <li><strong>Trip Scheduling:</strong> Available for immediate or future dates</li>
                    <li><strong>Pickup Time:</strong> As per scheduled departure time</li>
                    <li><strong>Service Duration:</strong> Based on trip distance and route</li>
                    <li><strong>Real-time Updates:</strong> Continuous during active trips</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Trip Types & Duration</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-800">Immediate Rides</h4>
                  <p className="text-gray-700 text-sm">Available within 15-30 minutes of booking confirmation</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Scheduled Rides</h4>
                  <p className="text-gray-700 text-sm">Can be booked up to 30 days in advance</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Long-Distance Rides</h4>
                  <p className="text-gray-700 text-sm">Duration varies based on distance (typically 1-12 hours)</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Pickup & Drop-off Services</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <div className="flex items-start space-x-3">
                <FaMapMarkerAlt className="text-green-600 text-2xl mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Location Services</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Pickup Location:</strong> As specified during booking</li>
                    <li><strong>Drop-off Location:</strong> As specified during booking</li>
                    <li><strong>Location Accuracy:</strong> GPS-based with address verification</li>
                    <li><strong>Route Optimization:</strong> Automatic route calculation for best path</li>
                    <li><strong>Real-time Tracking:</strong> Live location sharing during trip</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-700">
                <strong>Important:</strong> Passengers must be ready at the pickup location at the scheduled time. 
                Riders will wait for a maximum of 5 minutes. Delays may result in trip cancellation or additional charges.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Service Areas & Coverage</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Geographic Coverage</h3>
              <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                <li><strong>Primary Service Area:</strong> India (All states and union territories)</li>
                <li><strong>City Coverage:</strong> Major cities and towns across India</li>
                <li><strong>Inter-city Rides:</strong> Available between major cities</li>
                <li><strong>Rural Areas:</strong> Subject to rider availability</li>
                <li><strong>International:</strong> Not currently available</li>
              </ul>
            </div>
            <p className="text-gray-700">
              Service availability may vary based on rider presence in your area. We continuously work to expand 
              our coverage to serve more locations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Service Delivery Standards</h2>
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Quality Standards</h3>
                <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700 text-sm">
                  <li>Verified riders with valid licenses and documents</li>
                  <li>Vehicle verification and safety checks</li>
                  <li>Real-time trip tracking and updates</li>
                  <li>Secure payment processing</li>
                  <li>24/7 customer support</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Service Guarantees</h3>
                <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700 text-sm">
                  <li>Booking confirmation within seconds</li>
                  <li>Rider assignment within 15 minutes (for immediate rides)</li>
                  <li>Real-time location updates during trip</li>
                  <li>Secure and timely payment processing</li>
                  <li>Responsive customer support</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Delivery Timeline & Duration</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Service Duration Breakdown</h3>
              <table className="w-full text-sm text-gray-700">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Service Stage</th>
                    <th className="text-left py-2">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2">Booking Confirmation</td>
                    <td className="py-2">Instant (0-30 seconds)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Rider Assignment</td>
                    <td className="py-2">5-15 minutes (immediate rides)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Rider Arrival at Pickup</td>
                    <td className="py-2">15-30 minutes (immediate rides)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Trip Duration</td>
                    <td className="py-2">Varies by distance (calculated at booking)</td>
                  </tr>
                  <tr>
                    <td className="py-2">Payment Processing</td>
                    <td className="py-2">Instant (pre-paid) or post-trip</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Service Limitations</h2>
            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <h3 className="font-semibold text-gray-800 mb-2">Factors Affecting Service Delivery</h3>
              <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700 text-sm">
                <li>Rider availability in your area</li>
                <li>Weather conditions and natural disasters</li>
                <li>Traffic conditions and road closures</li>
                <li>Government restrictions or regulations</li>
                <li>Technical issues with platform or GPS</li>
                <li>Vehicle breakdowns or emergencies</li>
              </ul>
            </div>
            <p className="text-gray-700 text-sm">
              We strive to provide reliable service but cannot guarantee availability in all circumstances. 
              In case of service unavailability, full refunds will be provided.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Contact Information</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">For service delivery queries:</p>
              <p className="text-gray-700"><strong>Email:</strong> support@sastamastaride.com</p>
              <p className="text-gray-700"><strong>Phone:</strong> +91-1800-XXX-XXXX</p>
              <p className="text-gray-700"><strong>Address:</strong> [Your Registered Address], India</p>
            </div>
          </section>

          <div className="bg-blue-50 p-6 rounded-lg mt-8">
            <div className="flex items-start space-x-3">
              <FaInfoCircle className="text-blue-600 text-2xl mt-1" />
              <div>
                <p className="text-gray-700">
                  <strong>Important:</strong> This shipping and delivery policy is subject to change. We recommend 
                  reviewing it periodically. Continued use of our platform after policy changes constitutes acceptance 
                  of the new terms.
                </p>
                <p className="text-gray-700 mt-2">
                  For questions about this policy, contact us at{' '}
                  <a href="mailto:legal@sastamastaride.com" className="text-blue-600 hover:underline">
                    legal@sastamastaride.com
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;

